import { appConfig } from '@/config/env';
import { camelCaseSearchParams } from '@/config/searchParams';
import { createLogger } from '@/modules/logger';
import { QuizUserSession, readSavedFunnelUser, USER_STORAGE_KEY, type FunnelUser } from '@/modules/quiz/user';
import { createFunnelAnalytics, getCookieValue } from './index';

/**
 * One analytics instance for the whole funnel.
 *
 * Every route used to build its own: the quiz, the paywall and each of the four post-payment screens
 * called `init()` separately, and `init()` enqueues `Install` plus the `WebInstall` conversion — so a
 * single SPA run reported up to six installs. The install properties differed too: only the quiz sent
 * the utm parameters and the config metadata, so everything after it was attributed to nothing.
 *
 * This module owns what has to be shared: the user (one sign-in, one object the providers hold by
 * reference), the analytics core, and the single `init()`. Pages ask for it instead of making their own.
 */

export type FunnelAnalytics = ReturnType<typeof createFunnelAnalytics>;

let user: FunnelUser | undefined;
let signIn: Promise<FunnelUser> | undefined;
let analytics: FunnelAnalytics | undefined;
let initialized = false;

/**
 * A provider redirect (PayPal, Cash App, Stripe 3DS) reloads the page, which resets module state — so the
 * "already installed" fact lives in sessionStorage as well: one tab = one funnel run = one `Install`.
 */
export const INSTALL_STORAGE_KEY = '[[analytics-installed]]';

const readInstalled = (): boolean => {
  try {
    return sessionStorage.getItem(INSTALL_STORAGE_KEY) === '1';
  } catch {
    return false;
  }
};

const writeInstalled = (): void => {
  try {
    sessionStorage.setItem(INSTALL_STORAGE_KEY, '1');
  } catch {
    // Storage unavailable: the in-memory flag still covers this page load.
  }
};

const logger = () => createLogger(appConfig);

/** The object the providers keep a reference to; a late sign-in fills it in place. */
export const funnelUser = (): FunnelUser => (user ??= readSavedFunnelUser());

/**
 * The quiz's email, on the shared user: payments and conversions read it from here (WebPaywalls keeps it in
 * userStore). Persisted with the session so a provider redirect or a reload of the paywall keeps it.
 */
export const setFunnelUserEmail = (email: string | undefined): void => {
  const current = funnelUser();
  if (!email || current.email === email) return;
  current.email = email;
  try {
    const saved = JSON.parse(sessionStorage.getItem(USER_STORAGE_KEY) || 'null') as Partial<FunnelUser> | null;
    if (saved?.appUserId) sessionStorage.setItem(USER_STORAGE_KEY, JSON.stringify({ ...saved, email }));
  } catch {
    // Storage unavailable: the in-memory user still carries it for this page load.
  }
};

/**
 * Signs in once per app run. A saved session is reused by `QuizUserSession` itself, so a warm entry
 * costs nothing, and whichever page mounts first — quiz or paywall — pays for a cold one.
 */
export const ensureFunnelUser = (): Promise<FunnelUser> => {
  const current = funnelUser();
  signIn ??= new QuizUserSession(appConfig.apiUrl, appConfig.productName, appConfig.onboardingVariant, logger())
    .init()
    .then((signedIn) => {
      Object.assign(current, signedIn);
      return current;
    })
    .catch((error: unknown) => {
      logger().logWarning({ error: String(error) }, 'funnelUserSessionFailed');
      return current;
    });
  return signIn;
};

export const getFunnelAnalytics = (): FunnelAnalytics => {
  if (!analytics) {
    // The core waits for the sign-in before building providers: an empty user means anonymous
    // profiles, no backend provider and ignored per-user pixel ids.
    analytics = createFunnelAnalytics(appConfig, logger(), funnelUser(), {
      waitForUser: ensureFunnelUser(),
    });
  }
  return analytics;
};

/** Install properties, composed once — the set the quiz used to send alone. */
const installProperties = (extra: Record<string, unknown>): Record<string, unknown> => ({
  configsMetadata: appConfig.remoteConfigs.metadata,
  ...camelCaseSearchParams(appConfig.search),
  userAgent: typeof navigator === 'undefined' ? undefined : navigator.userAgent,
  productName: appConfig.productName,
  fbp: getCookieValue('_fbp'),
  fbc: getCookieValue('_fbc'),
  ...extra,
});

/**
 * Initialises analytics once. Later callers do not re-init — their page-specific properties are sent
 * as an update instead, so `Install` and `WebInstall` stay single while the profile still gains the
 * paywall variant and the onboarding settings.
 */
export const initFunnelAnalytics = (extra: Record<string, unknown> = {}): FunnelAnalytics => {
  const instance = getFunnelAnalytics();
  if (initialized || readInstalled()) {
    // Reload in the same tab: no second `Install`, but this page load's providers must be up.
    if (!initialized) void instance.initProviders();
    initialized = true;
    if (Object.keys(extra).length) {
      instance.pushUpdateProperties({ properties: extra } as never);
      void instance.sendEvents();
    }
    return instance;
  }
  initialized = true;
  writeInstalled();
  void instance.init(installProperties(extra));
  return instance;
};

/** Tests only: the module keeps app-wide state by design. */
export const resetFunnelAnalytics = (): void => {
  user = undefined;
  signIn = undefined;
  analytics = undefined;
  initialized = false;
};
